import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadGoogleIdentity } from '@/lib/googleIdentity';

interface GoogleSignInButtonProps {
  clientId: string;
  onCredential: (credential: string) => void;
  /** Google's own button wording: "Sign in with Google" or "Continue with Google". */
  text?: 'signin_with' | 'continue_with';
  /** Draw the button as wide as its column (Google caps it at 400 px). */
  fullWidth?: boolean;
}

/** Whether the page is drawn dark right now: the `.dark` class on <html> (lib/theme). */
function useRootIsDark(): boolean {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => setDark(root.classList.contains('dark')));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

/**
 * Google's own button (#2324), rendered by Google Identity Services into a
 * node we own. Google's brand rules require their button, not a lookalike, so
 * it carries Google's styling; `locale` follows the reader's language. Popup
 * mode: no redirect routes exist on our side. If the script cannot load, the
 * node stays empty and every other sign-in option still works.
 *
 * Dark mode (Victor 2026-10-21): Google's `filled_black` theme when the page
 * is dark, `outline` when light, redrawn when the theme changes. The node is
 * `color-scheme: light` because Google's button is an iframe: under the dark
 * page's `color-scheme` the browser painted the iframe's canvas opaque white,
 * a white box around the pill.
 */
export function GoogleSignInButton({ clientId, onCredential, text = 'signin_with', fullWidth = false }: GoogleSignInButtonProps) {
  const ref = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  const { i18n } = useTranslation();
  const dark = useRootIsDark();

  useEffect(() => {
    let cancelled = false;
    loadGoogleIdentity()
      .then(() => {
        const gis = window.google?.accounts.id;
        if (cancelled || !ref.current || !gis) return;
        gis.initialize({
          client_id: clientId,
          callback: (response) => onCredentialRef.current(response.credential),
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        ref.current.replaceChildren();
        gis.renderButton(ref.current, {
          type: 'standard',
          theme: dark ? 'filled_black' : 'outline',
          size: 'large',
          text,
          shape: 'pill',
          locale: i18n.language,
          ...(fullWidth ? { width: Math.min(400, Math.round(ref.current.getBoundingClientRect().width)) } : {}),
        });
      })
      .catch(() => { /* script blocked or offline: the button stays absent */ });
    return () => { cancelled = true; };
  }, [clientId, text, i18n.language, dark, fullWidth]);

  return (
    <div
      ref={ref}
      data-testid="google-signin-button"
      className={`min-h-[44px] [color-scheme:light] ${fullWidth ? 'flex w-full justify-center' : ''}`}
    />
  );
}
